import { defineStore } from "pinia";
import { PageModeType } from "@rmp/core/stores/appStore/types/pageModeType";
import Breadcrumb from "@rmp/core/stores/composables/page/types/breadcrumb";
import RequestCancelledException from "@rmp/core/exceptions/requestCancelledException";
import OfflineException from "@rmp/core/exceptions/offlineException";
import HttpNotFoundException from "@rmp/core/exceptions/httpNotFoundException";
import AccessForbiddenException from "@rmp/core/exceptions/accessForbiddenException";
import { normalizeError } from "@rmp/core/api/errors/classifyError";
import AlertHelper from "@rmp/core/stores/alerts/helpers/alertHelper";

export enum ErrorPresentationMode {
	Page = "page",
	Alert = "alert"
}

export interface AppState {
	pageMode: PageModeType;
	middlewareExecuted: boolean;
	middlewareFailed: boolean;
	breadcrumbs: Breadcrumb[];
	breadcrumbsLoading: boolean;
}

interface HandleServerErrorOptions {
	presentation: ErrorPresentationMode;
}

const getDefaultState = (): AppState => ({
	pageMode: PageModeType.OK,
	middlewareExecuted: true,
	middlewareFailed: false,
	breadcrumbs: [],
	breadcrumbsLoading: false
});

export const useAppStore = defineStore({
	id: "app",
	state: (): AppState => getDefaultState(),
	actions: {
		setMiddlewareExecuted(value: boolean) {
			this.middlewareExecuted = value;
		},
		resetPageMode() {
			this.pageMode = PageModeType.OK;
		},
		setPageModeNotFound() {
			this.pageMode = PageModeType.PAGE_NOT_FOUND;
		},
		setPageModeAccessForbidden() {
			this.pageMode = PageModeType.ACCESS_DENIED;
		},
		setPageModeInternalServerError() {
			this.pageMode = PageModeType.INTERNAL_SERVER_ERROR;
		},
		handleMiddlewareServerError(error: unknown) {
			this.middlewareFailed = true;
			this.handlePageLoadError(error);
		},
		handlePageLoadError(error: unknown)
		{
			this.handleServerError(error, {
				presentation: ErrorPresentationMode.Page
			});
		},
		
		handleActionError(error: unknown)
		{
			this.handleServerError(error, {
				presentation: ErrorPresentationMode.Alert
			});
		},
		
		handleServerError(
			error: unknown,
			options: HandleServerErrorOptions
		)
		{
			const classifiedError = normalizeError(error);
			
			console.error(classifiedError);
			
			if(classifiedError instanceof RequestCancelledException || classifiedError instanceof OfflineException)
				return;
			
			if(options.presentation === ErrorPresentationMode.Alert) {
				AlertHelper.handleGeneralRequestErrors(classifiedError);
			} else {
				this.handlePageError(classifiedError);
			}
		},
		
		handlePageError(error: Error)
		{
			if(error instanceof HttpNotFoundException) {
				this.setPageModeNotFound();
			} else if(error instanceof AccessForbiddenException) {
				this.setPageModeAccessForbidden();
			} else {
				// Остальные ошибки — включая Problem Details и недоступность сервера.
				this.setPageModeInternalServerError();
			}
		},
		setBreadcrumbs(value: Breadcrumb[]) {
			this.breadcrumbs = value;
		},
		setBreadcrumbsLoading(value: boolean) {
			this.breadcrumbsLoading = value;
		}
	}
});
